import { useEffect, useSyncExternalStore, type FC, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from './auth-context';
import type { AuthClient } from './auth-client';
import { getAuthClient } from './auth-runtime';
import { AuthPending } from './auth-pending';
import { AuthScreen } from './auth-screen';

interface AuthBoundaryProps {
  children: ReactNode;
  client?: AuthClient;
}

export const AuthBoundary: FC<AuthBoundaryProps> = ({
  children,
  client = getAuthClient()
}) => {
  const snapshot = useSyncExternalStore(client.subscribe, client.getSnapshot);
  const queryClient = useQueryClient();

  useEffect(() => {
    let session = client.getSnapshot().session;
    const unsubscribe = client.subscribe(() => {
      const next = client.getSnapshot().session;
      if (next !== session) {
        session = next;
        void queryClient.cancelQueries();
        queryClient.clear();
      }
    });
    void client.initialize();
    return unsubscribe;
  }, [client, queryClient]);

  // Restoring a cached session is not signing in: show the mark, not the sign-in page.
  if (snapshot.status === 'loading') return <AuthPending />;

  if (snapshot.status !== 'authenticated' || !snapshot.account) {
    return (
      <AuthScreen
        status={snapshot.status}
        message={snapshot.message}
        onSignIn={() => void client.signIn()}
        onReload={() => window.location.reload()}
      />
    );
  }

  return (
    <AuthContext
      value={{
        account: snapshot.account,
        mode: snapshot.mode,
        signOut: snapshot.mode === 'entra' ? client.signOut : undefined
      }}
    >
      <div key={snapshot.session} className="contents">
        {children}
      </div>
    </AuthContext>
  );
};
